import { type EnterpriseCustomWorkflow } from "@mg/schemas/src/commons/enterprise";
import {
  ClipboardText,
  FileMagnifyingGlass,
  Lightbulb,
  PaintBrush,
  ShieldCheck,
  Wrench,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { type ReactNode } from "react";

import { WORKFLOW_CARDS } from "../mockData";

interface SkillCardsProps {
  onCardClick: (workflowType: string) => void;
  enabledSkillIds?: string[];
  customWorkflows?: EnterpriseCustomWorkflow[];
}

const WORKFLOW_ICONS: Record<string, React.ReactNode> = {
  artwork_review: (
    <PaintBrush size={22} className="text-puntt-pink-9" weight="duotone" />
  ),
  brief_comparison: (
    <FileMagnifyingGlass
      size={22}
      className="text-puntt-accent-9"
      weight="duotone"
    />
  ),
  brief_creation: (
    <ClipboardText size={22} className="text-puntt-accent-9" weight="duotone" />
  ),
  compliance_review: (
    <ShieldCheck size={22} className="text-malachite-700" weight="duotone" />
  ),
  claim_generation: (
    <Lightbulb size={22} className="text-puntt-gold-9" weight="duotone" />
  ),
};

const WORKFLOW_ICON_BG: Record<string, string> = {
  artwork_review: "bg-puntt-pink-2 ring-1 ring-puntt-pink-5/50",
  brief_comparison: "bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50",
  brief_creation: "bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50",
  compliance_review: "bg-malachite-50 ring-1 ring-malachite-200/50",
  claim_generation: "bg-puntt-gold-2 ring-1 ring-puntt-gold-5/50",
};

const SKILL_CARD_INTERACTION =
  "shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-puntt-accent-7 hover:bg-puntt-blue-2 hover:shadow-lg";

type SkillCardLayout = "single" | "pair" | "grid";

function resolveSkillCardLayout(totalCards: number): SkillCardLayout {
  if (totalCards <= 1) return "single";
  if (totalCards === 2) return "pair";
  return "grid";
}

/** Max cards per row in grid layout; each row spans the full composer width. */
function resolveGridColumns(totalCards: number): number {
  return Math.min(totalCards, 4);
}

function skillCardShellClass(layout: SkillCardLayout) {
  return cx(
    "group flex rounded-2xl border border-puntt-neutral-gray-6 bg-base-white text-left",
    SKILL_CARD_INTERACTION,
    layout === "single" &&
      "w-full flex-row items-center gap-4 p-5 hover:translate-y-0",
    layout === "pair" &&
      "min-w-0 flex-1 basis-0 flex-row items-center gap-3 p-4 hover:translate-y-0",
    layout === "grid" && "min-w-0 flex-col gap-2 p-5",
  );
}

function skillCardsGridClass(layout: SkillCardLayout) {
  return cx("w-full gap-3", layout === "grid" ? "grid" : "flex");
}

function SkillCardBody({
  layout,
  icon,
  iconClassName,
  title,
  description,
}: {
  layout: SkillCardLayout;
  icon: ReactNode;
  iconClassName: string;
  title: string;
  description: string;
}) {
  const isHorizontal = layout === "single" || layout === "pair";

  const iconShell = (
    <div
      className={cx(
        "flex shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-105 group-hover:ring-2",
        layout === "single" ? "size-12" : "size-10",
        iconClassName,
      )}
    >
      {icon}
    </div>
  );

  const copy = (
    <div className={cx("min-w-0", isHorizontal && "flex-1")}>
      <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
        {title}
      </Text>
      <Text
        size="1"
        className={cx(
          "leading-relaxed text-puntt-neutral-gray-11",
          !isHorizontal && "line-clamp-2",
          isHorizontal && "line-clamp-3",
        )}
      >
        {description}
      </Text>
    </div>
  );

  return (
    <>
      {iconShell}
      {copy}
    </>
  );
}

/** @deprecated Use SkillCards instead */
export function WorkflowCards({
  onCardClick,
  enabledSkillIds,
  customWorkflows,
}: SkillCardsProps) {
  return (
    <SkillCards
      onCardClick={onCardClick}
      enabledSkillIds={enabledSkillIds}
      customWorkflows={customWorkflows}
    />
  );
}

export function SkillCards({
  onCardClick,
  enabledSkillIds,
  customWorkflows,
}: SkillCardsProps) {
  const filteredCards = enabledSkillIds
    ? WORKFLOW_CARDS.filter((card) =>
        enabledSkillIds.includes(card.workflowType),
      )
    : WORKFLOW_CARDS;

  const enabledCustomWorkflows = (customWorkflows ?? []).filter(
    (wf) => wf.enabled,
  );

  const totalCards = filteredCards.length + enabledCustomWorkflows.length;
  const layout = resolveSkillCardLayout(totalCards);
  const gridColumns = layout === "grid" ? resolveGridColumns(totalCards) : 0;
  const cardClassName = skillCardShellClass(layout);
  const gridStyle =
    layout === "grid"
      ? {
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
        }
      : undefined;

  return (
    <div
      className={skillCardsGridClass(layout)}
      style={gridStyle}
      data-testid="ask-puntt-skill-cards-grid"
      data-layout={layout}
      data-grid-columns={layout === "grid" ? gridColumns : undefined}
    >
      {filteredCards.map((card) => (
        <button
          key={card.id}
          data-testid={`ask-puntt-skill-card-${card.workflowType}`}
          onClick={() => onCardClick(card.id)}
          className={cardClassName}
        >
          <SkillCardBody
            layout={layout}
            icon={WORKFLOW_ICONS[card.id] ?? WORKFLOW_ICONS[card.workflowType]}
            iconClassName={
              WORKFLOW_ICON_BG[card.id] ??
              WORKFLOW_ICON_BG[card.workflowType] ??
              "bg-puntt-neutral-gray-2"
            }
            title={card.title}
            description={card.description}
          />
        </button>
      ))}
      {enabledCustomWorkflows.map((wf) => (
        <button
          key={wf.workflowId}
          data-testid={`ask-puntt-skill-card-custom_${wf.workflowId}`}
          onClick={() => onCardClick(`custom_${wf.workflowId}`)}
          className={cardClassName}
        >
          <SkillCardBody
            layout={layout}
            icon={
              <Wrench
                size={22}
                className="text-puntt-neutral-gray-11"
                weight="duotone"
              />
            }
            iconClassName="bg-puntt-neutral-gray-3 ring-1 ring-puntt-neutral-gray-5/50"
            title={wf.name}
            description={wf.trigger}
          />
        </button>
      ))}
    </div>
  );
}
